import React from 'react';
import { XCircle } from 'lucide-react';
import { Modal, Button, FormGroup, CategoryDropdown } from '../ui';
import ImageUpload from '../ui/ImageUpload';

const AddPropModal = ({ 
  showModal, 
  setShowModal, 
  editingProp, 
  propForm, 
  setPropForm, 
  saveProp, 
  deleteProp 
}) => {
  if (!showModal) return null;

  const handleMaterialKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value.trim();
      if (value && !propForm.material.includes(value)) {
        setPropForm({...propForm, material: [...propForm.material, value]});
        e.target.value = '';
      }
    }
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value.trim();
      if (value && !propForm.tags.includes(value)) {
        setPropForm({...propForm, tags: [...propForm.tags, value]});
        e.target.value = '';
      }
    }
  };

  const removeMaterial = (index) => {
    const newMaterials = propForm.material.filter((_, i) => i !== index);
    setPropForm({...propForm, material: newMaterials});
  };

  const removeTag = (index) => {
    const newTags = propForm.tags.filter((_, i) => i !== index);
    setPropForm({...propForm, tags: newTags});
  };

  return (
    <Modal 
      isOpen={showModal} 
      onClose={() => setShowModal(false)} 
      size="large"
      title={editingProp ? 'Edit Prop' : 'Add New Prop'}
    >
      <Modal.Content>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - PUBLIC Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2 text-green-700 bg-green-50 px-3 py-2 rounded">
                📋 PUBLIC Information
              </h3>

              <FormGroup label="Prop Name" required>
                <FormGroup.Input
                  value={propForm.name}
                  onChange={(e) => setPropForm({...propForm, name: e.target.value})}
                  placeholder="e.g., Victorian Velvet Armchair"
                />
              </FormGroup>

              <FormGroup label="Item Type" hint="Select the closest matching category">
                <CategoryDropdown
                  value={propForm.category_id}
                  onChange={(category_id) => setPropForm({...propForm, category_id})}
                  showAllOption={false}
                  placeholder="Select Category"
                />
              </FormGroup>

              <FormGroup label="Usage Category" hint="Reflects how the object is typically used">
                <CategoryDropdown
                  value={propForm.usage_category_id}
                  onChange={(usage_category_id) => setPropForm({...propForm, usage_category_id})}
                  showAllOption={false}
                  placeholder="Select Usage Category"
                />
              </FormGroup>

              <FormGroup.FieldGroup title="Visual Descriptors">
                <FormGroup label="Colors" hint="Add dominant colors (press Enter)">
                  <FormGroup.TagInput
                    tags={propForm.colors}
                    onAddTag={(color) => setPropForm({...propForm, colors: [...propForm.colors, color]})}
                    onRemoveTag={(index) => setPropForm({...propForm, colors: propForm.colors.filter((_, i) => i !== index)})}
                    placeholder="Add a color and press Enter"
                    tagColor="purple"
                  />
                </FormGroup>

                <FormGroup label="Size">
                  <FormGroup.Select
                    value={propForm.size}
                    onChange={(e) => setPropForm({...propForm, size: e.target.value})}
                    placeholder="Select size"
                    size="normal"
                  >
                    <option value="">Select size</option>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </FormGroup.Select>
                </FormGroup>

                <FormGroup label="Materials" hint="List primary materials">
                  <FormGroup.TagInput
                    tags={propForm.material}
                    onAddTag={(material) => setPropForm({...propForm, material: [...propForm.material, material]})}
                    onRemoveTag={(index) => removeMaterial(index)}
                    placeholder="Type material and press Enter"
                    tagColor="blue"
                  />
                </FormGroup>
              </FormGroup.FieldGroup>

              <FormGroup.FieldGroup title="Usage" variant="highlight">
                <FormGroup label="How would it be used?">
                  <FormGroup.Textarea
                    value={propForm.use_how}
                    onChange={(e) => setPropForm({...propForm, use_how: e.target.value})}
                    placeholder="Describe how the item is typically used"
                  />
                </FormGroup>
                <FormGroup label="When would it be used?">
                  <FormGroup.Textarea
                    value={propForm.use_when}
                    onChange={(e) => setPropForm({...propForm, use_when: e.target.value})}
                    placeholder="Describe the timeframe or seasonality"
                  />
                </FormGroup>
                <FormGroup label="Where would it be used?">
                  <FormGroup.Textarea
                    value={propForm.use_where}
                    onChange={(e) => setPropForm({...propForm, use_where: e.target.value})}
                    placeholder="Describe the typical location or setting"
                  />
                </FormGroup>
              </FormGroup.FieldGroup>
              
              <FormGroup label="Condition">
                <FormGroup.Select
                  value={propForm.condition}
                  onChange={(e) => setPropForm({...propForm, condition: e.target.value})}
                  placeholder="Select Condition"
                >
                  <option value="Excellent">Excellent</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                </FormGroup.Select>
              </FormGroup>

              <FormGroup label="Tags" hint="Type tag and press Enter">
                <FormGroup.TagInput
                  tags={propForm.tags}
                  onAddTag={(tag) => setPropForm({...propForm, tags: [...propForm.tags, tag]})}
                  onRemoveTag={(index) => removeTag(index)}
                  placeholder="Type tag and press Enter"
                  tagColor="green"
                />
              </FormGroup>

              <FormGroup.FieldGroup title="Historical Information" variant="highlight">
                <FormGroup label="Historical Period" hint="e.g., Victorian Era (1837-1901)">
                  <FormGroup.Input
                    value={propForm.historical_period}
                    onChange={(e) => setPropForm({...propForm, historical_period: e.target.value})}
                    placeholder="e.g., Victorian Era (1837-1901)"
                  />
                </FormGroup>
                
                <FormGroup label="Historical Region" hint="e.g., England">
                  <FormGroup.Input
                    value={propForm.historical_region}
                    onChange={(e) => setPropForm({...propForm, historical_region: e.target.value})}
                    placeholder="e.g., England"
                  />
                </FormGroup>
                
                <FormGroup.Checkbox
                  checked={Boolean(propForm.verified)}
                  onChange={(e) => setPropForm({...propForm, verified: e.target.checked})}
                  label="Historically Verified"
                />
              </FormGroup.FieldGroup>

              {/* Settings */}
              <div className="space-y-3">
                <h4 className="font-medium">Borrowing Settings</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(propForm.retention_agreement)}
                      onChange={(e) => setPropForm({...propForm, retention_agreement: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Retention Agreement</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(propForm.modification_allowed)}
                      onChange={(e) => setPropForm({...propForm, modification_allowed: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Modifications Allowed</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(propForm.transfer_available)}
                      onChange={(e) => setPropForm({...propForm, transfer_available: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Available for Transfer</span>
                  </label>
                </div>
              </div>

              {/* Images Section */}
              <div className="space-y-3 bg-blue-50 p-4 rounded-lg">
                <ImageUpload
                propId={editingProp?.id}
                existingImages={propForm.images || []}
                onImagesChange={(images) => {
                  console.log('📋 PropKioskDashboard received onImagesChange:', images);
                    setPropForm({...propForm, images});
                  console.log('📋 PropForm state updated, new images:', images);
                }}
                maxImages={5}
              />
              </div>

              {/* Consumable Section */}
              <div className="space-y-3 bg-orange-50 p-4 rounded-lg">
                <h4 className="font-medium text-orange-800">Consumable Items</h4>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(propForm.is_consumable)}
                    onChange={(e) => setPropForm({...propForm, is_consumable: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">This item is consumable</span>
                </label>
                {propForm.is_consumable && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Replacement Policy</label>
                    <select
                      value={propForm.consumable_policy}
                      onChange={(e) => setPropForm({...propForm, consumable_policy: e.target.value})}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="replace">Borrower must replace when consumed</option>
                      <option value="gone_when_gone">When it's gone, it's gone</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - WAREHOUSE ONLY Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2 text-red-700 bg-red-50 px-3 py-2 rounded">
                🔒 WAREHOUSE ONLY Information
              </h3>

              <div>
                <label className="block text-sm font-medium mb-1">Object Status</label>
                <select
                  value={propForm.status}
                  onChange={(e) => setPropForm({...propForm, status: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="available">Ready & Available</option>
                  <option value="checked_out">Checked Out</option>
                  <option value="under_repair">Undergoing Repair</option>
                  <option value="delicate">Delicate/Fragile - Limited Use</option>
                  <option value="disposal_soon">Being Thrown Away Soon</option>
                  <option value="lost">Lost</option>
                  <option value="retired">Retired/Archived</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Barcode/ID</label>
                <input
                  type="text"
                  value={propForm.barcode}
                  onChange={(e) => setPropForm({...propForm, barcode: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., VIC001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Purchase Date</label>
                <input
                  type="date"
                  value={propForm.purchase_date}
                  onChange={(e) => setPropForm({...propForm, purchase_date: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Private Notes</label>
                <textarea
                  value={propForm.private_notes}
                  onChange={(e) => setPropForm({...propForm, private_notes: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-20"
                  placeholder="Internal notes visible only to warehouse staff..."
                />
              </div>

              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Transfer Coordination:</strong> Transfer costs and timing will be set by service providers when transfer requests are made.
                </p>
              </div>

              {/* Checkout History Section - Only show when editing existing prop */}
              {editingProp && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <details className="group">
                    <summary className="font-medium cursor-pointer flex items-center gap-2">
                      <span className="transform transition-transform group-open:rotate-90">▶</span>
                      Checkout History
                    </summary>
                    <div className="mt-3 space-y-2">
                      <div className="text-sm text-gray-600">
                        <div className="bg-white p-3 rounded border">
                          <div className="flex justify-between">
                            <span>Sample Checkout #1</span>
                            <span>Oct 15-22, 2024</span>
                          </div>
                          <div className="text-xs text-gray-500">By: Austin Community Theatre • For: Hamlet Production</div>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <div className="flex justify-between">
                            <span>Sample Checkout #2</span>
                            <span>Sep 3-10, 2024</span>
                          </div>
                          <div className="text-xs text-gray-500">By: University Drama Dept • For: Romeo & Juliet</div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 italic">Checkout history will be automatically populated from actual usage</p>
                    </div>
                  </details>
                </div>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
        </Modal.Content>
        
        <Modal.Footer className="flex justify-between items-center pb-20">
          <div>
            {editingProp && (
              <Button
                onClick={() => deleteProp(editingProp.id)}
                variant="danger"
                size="compact"
              >
                <XCircle className="h-3 w-3" />
                Delete Prop
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowModal(false)}
              variant="secondary"
              size="compact"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                console.log('💾 Save button clicked!');
                saveProp();
              }}
              disabled={!propForm.name}
              variant="primary"
              size="compact"
            >
              {editingProp ? 'Update Prop' : 'Add Prop'}
            </Button>
          </div>
        </Modal.Footer>
    </Modal>
  );
};

export default AddPropModal;
